import type { EnumOption } from '@core/utils/enum';
import { AlbLaceOption, AlbLaceStyle } from '../enum/albLace';
import { AlbSubtype } from '../enum/albSubtype';
import { VestmentType, isV1VestmentType } from '../enum/vestmentType';

type AlbSizeField =
  | 'length'
  | 'shoulder'
  | 'sleeve'
  | 'neck'
  | 'chest'
  | 'waist'
  | 'figure';

const FULL: AlbSizeField[] = [
  'length',
  'shoulder',
  'sleeve',
  'neck',
  'chest',
  'waist',
  'figure',
];
const ROMAN: AlbSizeField[] = ['length', 'shoulder', 'sleeve', 'neck', 'chest'];
const SURPLICE: AlbSizeField[] = ['length', 'shoulder', 'sleeve', 'chest'];
const V1_SURPLICE: AlbSizeField[] = ['length', 'shoulder', 'sleeve', 'neck'];
const SHARED_DESIGNS = [VestmentType.AlbOverlapping];

export const ALB_BODY_FABRIC_NAME = 'Miyaki';
export const ALB_BODY_COLOR_NAME = 'White';

export const ALB_SUBTYPE_OPTIONS: EnumOption[] = [
  { value: AlbSubtype.Embroidery, label: 'With Embroidery' },
  { value: AlbSubtype.Lace, label: 'With Lace' },
  { value: AlbSubtype.Hugot, label: 'With Hugot Design' },
  { value: AlbSubtype.Plain, label: 'Plain' },
];

export function getAlbSubtypeLabel(subtype?: string | null): string | null {
  return (
    ALB_SUBTYPE_OPTIONS.find((option) => option.value === subtype)?.label ??
    null
  );
}

export const ALB_LACE_STYLE_OPTIONS: EnumOption[] = [
  { value: AlbLaceStyle.Insert, label: 'Lace Insert' },
  { value: AlbLaceStyle.Full, label: 'Full Lace' },
];

export const ALB_LACE_INSERT_OPTIONS: EnumOption[] = [
  { value: AlbLaceOption.JhsInsert, label: 'JHS Insert' },
  { value: AlbLaceOption.AlphaOmegaInsert, label: 'Alpha Omega Insert' },
  { value: AlbLaceOption.CrossDesignInsert, label: 'Cross Design Insert' },
  { value: AlbLaceOption.MarianDesignInsert, label: 'Marian Design Insert' },
];

export const ALB_LACE_FULL_OPTIONS: EnumOption[] = [
  { value: AlbLaceOption.Jhs, label: 'JHS Lace' },
  { value: AlbLaceOption.MarianDesign, label: 'Marian Design Lace' },
  { value: AlbLaceOption.AlphaOmegaDesign, label: 'Alpha Omega Design Lace' },
  { value: AlbLaceOption.Long, label: 'Long Lace' },
];

export function getAlbLaceStyleLabel(style?: string | null): string | null {
  return (
    ALB_LACE_STYLE_OPTIONS.find((option) => option.value === style)?.label ??
    null
  );
}

export function albLaceOptions(style?: string | null): EnumOption[] {
  if (style === AlbLaceStyle.Insert) {
    return ALB_LACE_INSERT_OPTIONS;
  }
  if (style === AlbLaceStyle.Full) {
    return ALB_LACE_FULL_OPTIONS;
  }
  return [];
}

export function albLaceOptionMatches(
  style?: string | null,
  option?: string | null
): boolean {
  return albLaceOptions(style).some((item) => item.value === option);
}

export function getAlbLaceOptionLabel(option?: string | null): string | null {
  return (
    [...ALB_LACE_INSERT_OPTIONS, ...ALB_LACE_FULL_OPTIONS].find(
      (item) => item.value === option
    )?.label ?? null
  );
}

export function getAlbPreviewSubtype<T extends { kind: string }>(
  subtypes: T[] | null | undefined,
  subtype?: string | null,
  laceStyle?: string | null
): T | null {
  let kind: string | null = null;
  if (
    subtype === AlbSubtype.Embroidery ||
    subtype === AlbSubtype.Hugot ||
    subtype === AlbSubtype.Plain
  ) {
    kind = subtype;
  } else if (subtype === AlbSubtype.Lace && laceStyle === AlbLaceStyle.Insert) {
    kind = 'lace_insert';
  } else if (subtype === AlbSubtype.Lace && laceStyle === AlbLaceStyle.Full) {
    kind = 'lace_full';
  }
  if (kind === null) {
    return null;
  }
  return subtypes?.find((item) => item.kind === kind) ?? null;
}

export function albDesignSection(subtype?: string | null): {
  design: boolean;
  thread: boolean;
} | null {
  if (subtype === AlbSubtype.Embroidery) {
    return { design: true, thread: false };
  }
  if (subtype === AlbSubtype.Hugot) {
    return { design: true, thread: true };
  }
  return null;
}

export type AlbFeatures = {
  hasSubtypes: boolean;
  sizeFields: AlbSizeField[];
  designTypes: VestmentType[];
  collar: boolean;
  lengthOffset: number;
};

function features(
  hasSubtypes: boolean,
  sizeFields: AlbSizeField[],
  designTypes: VestmentType[],
  collar = false,
  lengthOffset = 0
): AlbFeatures {
  return { hasSubtypes, sizeFields, designTypes, collar, lengthOffset };
}

const ALB_FEATURES: Partial<Record<VestmentType, AlbFeatures>> = {
  [VestmentType.AlbOverlapping]: features(true, FULL, SHARED_DESIGNS),
  [VestmentType.AlbPoland]: features(true, FULL, SHARED_DESIGNS),
  [VestmentType.AlbAmerican]: features(false, FULL, []),
  [VestmentType.AlbMonastic]: features(false, FULL, []),
  [VestmentType.AlbV1Roman]: features(
    false,
    FULL,
    [VestmentType.AlbV1Roman],
    true,
    1
  ),
  [VestmentType.AlbV1NonRoman]: features(
    false,
    FULL,
    [VestmentType.AlbV1NonRoman],
    true
  ),
  [VestmentType.AlbRoman]: features(true, ROMAN, SHARED_DESIGNS, false, 1),
  [VestmentType.AlbRomanSideZipper]: features(
    true,
    ROMAN,
    SHARED_DESIGNS,
    false,
    1
  ),
  [VestmentType.AlbSurplice]: features(
    true,
    SURPLICE,
    SHARED_DESIGNS,
    false,
    1
  ),
  [VestmentType.AlbV1Surplice]: features(
    false,
    V1_SURPLICE,
    [VestmentType.AlbV1Surplice],
    true
  ),
};

export function getAlbFeatures(
  vestmentType?: VestmentType | null
): AlbFeatures | undefined {
  return vestmentType ? ALB_FEATURES[vestmentType] : undefined;
}

export function usesAlbBodyDefault(
  vestmentType?: VestmentType | null
): boolean {
  return (
    !!vestmentType &&
    !isV1VestmentType(vestmentType) &&
    !!getAlbFeatures(vestmentType)
  );
}
